import {
  Box,
  Text,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useDisclosure,
  VStack,
  Flex,
} from "@chakra-ui/react"
import axios from "axios"
import React from "react"
import { api } from "config"
import { Group } from "types/apiTypes"

const rankLabels = ["Gold", "Silver", "Bronze"]
const medalIcons = ["🥇", "🥈", "🥉"]

const ringColors = [
  "linear-gradient(135deg,#FFD700,#FFB347)",
  "linear-gradient(135deg,#D1D5DB,#9CA3AF)",
  "linear-gradient(135deg,#CD7F32,#E6A96B)",
]

const StudentRanksTicker = () => {
  const [groupClasses, setGroupClasses] = React.useState<Group[]>([])
  const [students, setStudents] = React.useState<any[]>([])
  const [activeRank, setActiveRank] = React.useState(0)
  const [selectedStudent, setSelectedStudent] = React.useState<any>(null)
  const [loadingMedals, setLoadingMedals] = React.useState(false)

  const { isOpen, onOpen, onClose } = useDisclosure()

  React.useEffect(() => {
    axios
      .get(`${api}/groups/my-classes`, { withCredentials: true })
      .then((res) => setGroupClasses(res.data))
  }, [])

  React.useEffect(() => {
    if (!groupClasses.length) return

    axios
      .post(
        `${api}/groups/my-students`,
        { groupIds: groupClasses.map((g) => g.id) },
        { withCredentials: true },
      )
      .then((res) => setStudents(res.data))
  }, [groupClasses])

  const rankedStudents = React.useMemo(() => {
    if (!students.length) return []

    const sorted = [...students].sort((a, b) => b.marks - a.marks)

    return [
      { rank: 0, students: sorted.slice(0, 5) }, // top students
      { rank: 1, students: sorted.slice(5, 10) },
      { rank: 2, students: sorted.slice(10, 15) },
    ]
  }, [students])

  if (!rankedStudents.length) return null

  const current = rankedStudents[activeRank]

  const openStudent = async (student: { id: any }) => {
    setSelectedStudent({ ...student, gold: null, silver: null, bronze: null })
    setLoadingMedals(true)
    onOpen()

    try {
      const res = await axios.get(`${api}/students/${student.id}/medals`, {
        withCredentials: true,
      })

      setSelectedStudent((prev: any) => ({
        ...prev,
        gold: res.data.gold,
        silver: res.data.silver,
        bronze: res.data.bronze,
      }))
    } catch (err) {
      console.error("Failed to load medals", err)
    } finally {
      setLoadingMedals(false)
    }
  }

  return (
    <>
      <Box display={{ base: "block", md: "none" }} px={3}>
        <HStack bg="white" p={1} borderRadius="full" justify="center" mb={3}>
          {rankLabels.map((label, i) => (
            <Box
              key={label}
              px={4}
              py={1.5}
              fontSize="sm"
              fontWeight="bold"
              borderRadius="full"
              cursor="pointer"
              bg={activeRank === i ? "black" : "transparent"}
              color={activeRank === i ? "white" : "gray.600"}
              onClick={() => setActiveRank(i)}
            >
              {medalIcons[i]} {label}
            </Box>
          ))}
        </HStack>

        <Box overflowX="auto" pb={2}>
          <HStack spacing={4} key={activeRank}>
            {current.students.map((student: any) => {
              const name = student.name || student.username || "S"
              const initial = name.charAt(0).toUpperCase()

              return (
                <Box
                  key={student.id}
                  textAlign="center"
                  minW="78px"
                  cursor="pointer"
                  onClick={() => openStudent(student)}
                >
                  <Box
                    p="3px"
                    borderRadius="full"
                    bg={ringColors[current.rank]}
                  >
                    <Box
                      w="64px"
                      h="64px"
                      borderRadius="full"
                      overflow="hidden"
                      bg="gray.200"
                      border="3px solid white"
                    >
                      {student.image ? (
                        <img
                          src={student.image}
                          alt={name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          w="100%"
                          h="100%"
                          bg="gray.700"
                          color="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="lg"
                        >
                          {initial}
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Text
                    fontSize="xs"
                    mt={1}
                    maxW="78px"
                    noOfLines={1}
                    textAlign="center"
                  >
                    {name}
                  </Text>
                </Box>
              )
            })}
          </HStack>
        </Box>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay backdropFilter="blur(6px)" />
        <ModalContent borderRadius="xl" p={4} maxW="320px">
          <ModalBody>
            {selectedStudent && (
              <Flex align="center" gap={4}>
                <Box w="90px" h="90px" borderRadius="full" overflow="hidden">
                  {selectedStudent.image ? (
                    <img
                      src={selectedStudent.image}
                      alt={selectedStudent.name || selectedStudent.username}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box
                      w="100%"
                      h="100%"
                      bg="gray.700"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      fontSize="2xl"
                    >
                      {(selectedStudent.name || selectedStudent.username || "S")
                        .charAt(0)
                        .toUpperCase()}
                    </Box>
                  )}
                </Box>

                <VStack align="start" spacing={2}>
                  <Text fontWeight="bold">
                    {selectedStudent.name || selectedStudent.username}
                  </Text>

                  <HStack>
                    {loadingMedals ? (
                      <Text fontSize="sm" color="gray.500">
                        Loading medals...
                      </Text>
                    ) : (
                      <HStack>
                        <Box bg="gray.100" px={2} py={1} borderRadius="md">
                          🥇 {selectedStudent.gold}
                        </Box>
                        <Box bg="gray.100" px={2} py={1} borderRadius="md">
                          🥈 {selectedStudent.silver}
                        </Box>
                        <Box bg="gray.100" px={2} py={1} borderRadius="md">
                          🥉 {selectedStudent.bronze}
                        </Box>
                      </HStack>
                    )}
                  </HStack>
                </VStack>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default StudentRanksTicker
